/**
 * MietwagenRueckgabeProtokoll — Issue #473
 *
 * Mobile-First Protokoll-Komponente für die Rückgabe von Mietfahrzeugen.
 * Vergleichbar mit SchluesselausleihProtokoll (pool vehicles), aber für
 * Unfallersatzfahrzeuge (Mietwagen-Akten):
 *   • Kilometerstand, Tankstand, Schäden, Rückgabedatum, unterschreibende Person
 *   • Foto-Upload in Storage-Bucket akten-dokumente
 *   • Persistiert Felder direkt auf der Akte (akten-Tabelle)
 *   • Blob-URL-Cleanup analog SchluesselausleihProtokoll (Issue #225)
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import SignaturePad from 'signature_pad';
import { Camera, Check, Loader2, Pen, Trash2, X, Fuel, Gauge } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { getRueckgabeFotoStoragePath, getRueckgabeStoragePath } from '@/lib/mietwagen';

interface MietwagenRueckgabeProtokollProps {
  akteId: string;
  onComplete?: () => void;
}

interface AkteMini {
  id: string;
  akten_nummer: string | null;
  kunde_name: string | null;
  mietfahrzeug_kennzeichen: string | null;
  mietfahrzeug_marke: string | null;
  mietfahrzeug_modell: string | null;
  mietfahrzeug_km_start: number | null;
  mietfahrzeug_tankstand_start: string | null;
}

const TANKSTAND_OPTIONS = [
  { value: 'voll', label: 'Voll' },
  { value: '3/4', label: '3/4' },
  { value: '1/2', label: '1/2' },
  { value: '1/4', label: '1/4' },
  { value: 'leer', label: 'Leer' },
] as const;

function safeFilename(value: string): string {
  return value.replace(/[^a-z0-9._-]/gi, '_');
}

function todayIso(): string {
  return new Date().toISOString().split('T')[0];
}

export default function MietwagenRueckgabeProtokoll({
  akteId,
  onComplete,
}: MietwagenRueckgabeProtokollProps) {
  const [akte, setAkte] = useState<AkteMini | null>(null);
  const [loadingAkte, setLoadingAkte] = useState(true);

  const [kmEnde, setKmEnde] = useState('');
  const [tankstand, setTankstand] = useState<string>('voll');
  const [rueckgabeDatum, setRueckgabeDatum] = useState(todayIso());
  const [schaeden, setSchaeden] = useState('');
  const [fotos, setFotos] = useState<File[]>([]);
  const [unterschreibendePerson, setUnterschreibendePerson] = useState('');
  const [hasUnterschrift, setHasUnterschrift] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const sigCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const sigPadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function loadAkte() {
      const { data, error } = await supabase
        .from('akten')
        .select(
          'id, akten_nummer, kunde_name, mietfahrzeug_kennzeichen, mietfahrzeug_marke, mietfahrzeug_modell, mietfahrzeug_km_start, mietfahrzeug_tankstand_start',
        )
        .eq('id', akteId)
        .single();
      if (!cancelled) {
        if (error) {
          toast.error('Akte konnte nicht geladen werden');
        } else {
          setAkte(data as AkteMini);
        }
        setLoadingAkte(false);
      }
    }
    loadAkte();
    return () => {
      cancelled = true;
    };
  }, [akteId]);

  useEffect(() => {
    const canvas = sigCanvasRef.current;
    if (!canvas) return;
    const ratio = Math.max(window.devicePixelRatio || 1, 1);
    canvas.width = canvas.offsetWidth * ratio;
    canvas.height = canvas.offsetHeight * ratio;
    canvas.getContext('2d')?.scale(ratio, ratio);
    const pad = new SignaturePad(canvas, {
      backgroundColor: 'rgb(255,255,255)',
      penColor: 'rgb(20,20,20)',
    });
    pad.addEventListener('endStroke', () => {
      setHasUnterschrift(!pad.isEmpty());
    });
    sigPadRef.current = pad;
    return () => {
      pad.off();
      sigPadRef.current = null;
    };
  }, [loadingAkte]);

  const clearUnterschrift = useCallback(() => {
    sigPadRef.current?.clear();
    setHasUnterschrift(false);
  }, []);

  const previews = useMemo(
    () => fotos.map((f) => ({ name: f.name, url: URL.createObjectURL(f) })),
    [fotos],
  );

  useEffect(() => {
    return () => {
      previews.forEach((p) => URL.revokeObjectURL(p.url));
    };
  }, [previews]);

  const handleFiles = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    setFotos((prev) => [...prev, ...Array.from(list)]);
  };

  const removeFoto = (index: number) => {
    setFotos((prev) => prev.filter((_, i) => i !== index));
  };

  const fahrzeugLabel = useMemo(() => {
    if (!akte) return '—';
    const kennzeichen = akte.mietfahrzeug_kennzeichen || '—';
    const typ = [akte.mietfahrzeug_marke, akte.mietfahrzeug_modell]
      .filter(Boolean)
      .join(' ');
    return typ ? `${kennzeichen} (${typ})` : kennzeichen;
  }, [akte]);

  const handleSpeichern = async () => {
    if (!akte) return;

    if (!kmEnde.trim() || Number.isNaN(Number(kmEnde))) {
      toast.error('Bitte KM-Stand bei Rückgabe angeben');
      return;
    }
    if (!rueckgabeDatum) {
      toast.error('Bitte Rückgabedatum angeben');
      return;
    }
    const pad = sigPadRef.current;
    if (!pad || pad.isEmpty()) {
      toast.error('Bitte Unterschrift erfassen');
      return;
    }

    setIsSaving(true);
    try {
      // ── Unterschrift hochladen ──
      const sigDataUrl = pad.toDataURL('image/png');
      const sigBlob = await fetch(sigDataUrl).then((r) => r.blob());
      const sigFilename = `unterschrift_${Date.now()}.png`;
      const sigPath = getRueckgabeStoragePath(akte, sigFilename);
      const { error: sigErr } = await supabase.storage
        .from('akten-dokumente')
        .upload(sigPath, sigBlob, { contentType: 'image/png', upsert: false });
      let signaturUrl: string | null = null;
      if (sigErr) {
        console.error('Unterschrift-Upload fehlgeschlagen:', sigErr);
        toast.error('Unterschrift konnte nicht gespeichert werden');
        return;
      } else {
        const { data: sigUrlData } = supabase.storage
          .from('akten-dokumente')
          .getPublicUrl(sigPath);
        signaturUrl = sigUrlData.publicUrl;
      }

      // ── Fotos hochladen ──
      const fotoUrls: string[] = [];
      for (const file of fotos) {
        const filename = `${Date.now()}_${safeFilename(file.name)}`;
        const fotoPath = getRueckgabeFotoStoragePath(akte, filename);
        const { error: fotoErr } = await supabase.storage
          .from('akten-dokumente')
          .upload(fotoPath, file, { upsert: false });
        if (fotoErr) {
          console.error('Foto-Upload fehlgeschlagen:', fotoErr);
          toast.error(`Foto ${file.name} konnte nicht hochgeladen werden`);
          continue;
        }
        const { data: urlData } = supabase.storage
          .from('akten-dokumente')
          .getPublicUrl(fotoPath);
        fotoUrls.push(urlData.publicUrl);
      }

      // ── Akte aktualisieren ──
      const updates: Record<string, unknown> = {
        mietfahrzeug_km_ende: Number(kmEnde),
        mietfahrzeug_tankstand_ende: tankstand,
        rueckgabe_datum: rueckgabeDatum,
        rueckgabe_schaeden: schaeden.trim() || null,
        rueckgabe_protokoll_url: signaturUrl,
        rueckgabe_unterschrift_person: unterschreibendePerson.trim() || null,
      };
      if (fotoUrls.length > 0) {
        updates.rueckgabe_fotos_url = fotoUrls.join(',');
      }

      const { error: updErr } = await supabase
        .from('akten')
        .update(updates)
        .eq('id', akte.id);

      if (updErr) {
        console.error('Akte-Update fehlgeschlagen:', updErr);
        toast.error('Rückgabe konnte nicht gespeichert werden');
        return;
      }

      toast.success('Rückgabe protokolliert', {
        description: `${fahrzeugLabel} — ${rueckgabeDatum}`,
      });
      setFotos([]);
      onComplete?.();
    } catch (err) {
      console.error('Rueckgabe-Protokoll fehlgeschlagen:', err);
      toast.error('Unerwarteter Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  if (loadingAkte) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="size-6 animate-spin text-orange-500" />
      </div>
    );
  }

  return (
    <div className="w-full max-w-xl mx-auto p-4 space-y-6">
      <header className="space-y-1">
        <h2 className="text-lg font-semibold">Rückgabe-Protokoll</h2>
        <p className="text-sm text-muted-foreground">{fahrzeugLabel}</p>
      </header>

      {/* ── Datum ── */}
      <section className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Rückgabedatum</label>
        <input
          data-testid="rueckgabe-protokoll-datum"
          type="date"
          value={rueckgabeDatum}
          onChange={(e) => setRueckgabeDatum(e.target.value)}
          className="w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm"
        />
      </section>

      {/* ── KM-Stand ── */}
      <section className="space-y-2">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
          <Gauge className="size-4 text-gray-600" />
          KM-Stand bei Rückgabe
        </label>
        {akte?.mietfahrzeug_km_start != null && (
          <p className="text-xs text-gray-400">
            Bei Übergabe: {akte.mietfahrzeug_km_start.toLocaleString('de-DE')} km
          </p>
        )}
        <input
          data-testid="rueckgabe-protokoll-km"
          type="number"
          inputMode="numeric"
          value={kmEnde}
          onChange={(e) => setKmEnde(e.target.value)}
          placeholder="z.B. 12345"
          className="w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-base"
        />
      </section>

      {/* ── Tankstand ── */}
      <section className="space-y-3">
        <div className="flex items-center gap-2">
          <Fuel className="size-4 text-gray-600" />
          <h3 className="text-sm font-medium">Tankstand bei Rückgabe</h3>
        </div>
        {akte?.mietfahrzeug_tankstand_start && (
          <p className="text-xs text-gray-400">
            Bei Übergabe: {akte.mietfahrzeug_tankstand_start}
          </p>
        )}
        <div data-testid="rueckgabe-protokoll-tankstand" className="grid grid-cols-5 gap-2">
          {TANKSTAND_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setTankstand(opt.value)}
              aria-pressed={tankstand === opt.value}
              className={`h-11 rounded-lg border text-sm font-medium transition-colors ${
                tankstand === opt.value
                  ? 'bg-orange-600 text-white border-orange-600'
                  : 'bg-white text-gray-700 border-gray-200'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </section>

      {/* ── Schäden ── */}
      <section className="space-y-2">
        <label className="text-sm font-medium text-gray-700">Schäden bei Rückgabe</label>
        <textarea
          data-testid="rueckgabe-protokoll-schaden"
          value={schaeden}
          onChange={(e) => setSchaeden(e.target.value)}
          placeholder="Beschreibung eventueller neuer Schäden…"
          rows={3}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm"
        />
      </section>

      {/* ── Fotos ── */}
      <section className="space-y-3">
        <label className="flex items-center justify-center gap-2 h-14 rounded-xl border-2 border-dashed border-gray-300 bg-white cursor-pointer active:scale-[0.99] transition-transform">
          <Camera className="size-5 text-gray-600" />
          <span className="text-sm font-medium text-gray-700">
            {fotos.length === 0
              ? 'Fotos aufnehmen (Schäden, Fahrzeug)'
              : `Weitere Fotos (${fotos.length} gewählt)`}
          </span>
          <input
            data-testid="rueckgabe-protokoll-foto-input"
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(e) => handleFiles(e.target.files)}
          />
        </label>

        {previews.length > 0 && (
          <ul className="grid grid-cols-3 gap-2">
            {previews.map((p, i) => (
              <li
                key={p.url}
                className="relative aspect-square rounded-lg overflow-hidden border bg-gray-100"
              >
                <img
                  src={p.url}
                  alt={`Foto ${i + 1}`}
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  aria-label={`Foto ${i + 1} entfernen`}
                  onClick={() => removeFoto(i)}
                  className="absolute top-1 right-1 size-6 rounded-full bg-black/60 text-white flex items-center justify-center"
                >
                  <X className="size-3.5" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* ── Unterschrift ── */}
      <section className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-gray-700">Unterschrift Kunde</label>
          <button
            type="button"
            onClick={clearUnterschrift}
            className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
          >
            <Trash2 className="size-3" />
            Zurücksetzen
          </button>
        </div>
        <input
          data-testid="rueckgabe-protokoll-unterschrift"
          type="text"
          value={unterschreibendePerson}
          onChange={(e) => setUnterschreibendePerson(e.target.value)}
          placeholder="Name der unterschreibenden Person"
          className="w-full h-11 rounded-lg border border-gray-200 bg-white px-3 text-sm"
        />
        <div className="rounded-xl border border-gray-300 bg-white relative">
          <canvas
            ref={sigCanvasRef}
            className="w-full h-36 rounded-xl touch-none"
            aria-label="Unterschrift-Canvas"
          />
          {!hasUnterschrift && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-gray-300 text-sm">
              <Pen className="size-4 mr-1" /> Hier unterschreiben
            </div>
          )}
        </div>
      </section>

      {/* ── Speichern ── */}
      <button
        data-testid="rueckgabe-protokoll-speichern"
        type="button"
        onClick={handleSpeichern}
        disabled={isSaving}
        className="w-full h-12 flex items-center justify-center gap-2 bg-orange-600 text-white rounded-xl font-semibold text-sm hover:bg-orange-700 transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
      >
        {isSaving ? (
          <>
            <Loader2 className="size-4 animate-spin" />
            Speichere…
          </>
        ) : (
          <>
            <Check className="size-4" />
            Rückgabe protokollieren
          </>
        )}
      </button>
    </div>
  );
}
